/* eslint-disable eqeqeq */
import _ from "lodash";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../api/api";
import useUrlQuery from "../hooks/useUrlQuery";
import { Actions } from "../slices/actions";
import {
  compareQuoteSelector,
  filtersSelector,
  knowMoreSelector,
  shortListedQuotesSelector,
} from "../slices/selectors";
import { useGetUserDetails } from "./globalService";

export const useGetPlans = () => {
  const { getQuery } = useUrlQuery();
  const enquiry_id = getQuery("enquiry_id");
  const { userDetails } = useGetUserDetails();
  const { sum_insured } = useSelector(filtersSelector);

  const plans = useQueries(
    Object.keys(userDetails?.company_details || []).map((company) => ({
      queryKey: ["Plans", company, sum_insured],
      queryFn: () =>
        api.post(`${company}/getPlans`, {
          sum_insured,
          enquiry_id,

          ...(company === "reliance_general" && { company }),
        }),
    }))
  );
  return {
    plansData: plans
      .map((data) => data.data?.data.plan_data)
      .flat()
      .filter((item) => item),
    isPlanLoading: plans.some((item) => item.isLoading),
  };
};
export const useGetQuotes = () => {
  const { userDetails } = useGetUserDetails();
  const { proposer_age } = userDetails;

  const { getQuery } = useUrlQuery();
  const enquiry_id = getQuery("enquiry_id");
  const { isPlanLoading, plansData } = useGetPlans();

  const { insurers, sortBy } = useSelector(filtersSelector);
  const allQuotesData = useQueries(
    plansData?.map((plan) => ({
      queryKey: ["Plans", plan?.id, plan?.company_alias, plan?.sum_insured],
      queryFn: () =>
        api.post(`${plan.company_alias}/getPremium`, {
          enquiry_id,
          plan_data: plan,
        }),
      enabled: !(proposer_age < 18 && plan.company_alias === "tata_aig"),
    }))
  );
  const isHighToLow = sortBy === "High To Low";
  const quotes = _.groupBy(
    allQuotesData
      ? allQuotesData.map((data) =>
          data.error || !data?.data?.data?.status
            ? {
                quoteData: { ic_data: [{ id: 0 }], data: [], status: false },
                isQuoteLoading: data?.isLoading,
              }
            : { quoteData: data?.data?.data, isQuoteLoading: data?.isLoading }
        )
      : [],
    (data) => data?.quoteData?.ic_data[0]?.id
  );
  const sortedKeys = _.uniq(
    allQuotesData
      ? allQuotesData
          .filter((item) => {
            return (
              !insurers.length ||
              insurers.includes(String(item?.data?.data.ic_data[0].id))
            );
          })
          .sort((a, b) => {
            if (!a.data?.data?.data) return 1;
            else if (!b.data?.data?.data) return -1;
            else
              return isHighToLow
                ? b?.data?.data?.data[0]?.total_premium_amount -
                    a?.data?.data?.data[0]?.total_premium_amount
                : a?.data?.data?.data[0]?.total_premium_amount -
                    b?.data?.data?.data[0]?.total_premium_amount;
          })
          .map((item) => {
            return item.error || !item?.data?.data?.status
              ? 0
              : item?.data?.data?.ic_data[0]?.id;
          })
      : []
  );
  const quotesByInsurer = _.groupBy(
    allQuotesData
      ? allQuotesData
          .filter((item) => {
            return (
              item?.data?.data.status &&
              (!insurers.length ||
                insurers.includes(String(item?.data?.data.ic_data[0].id)))
            );
          })
          .map((data) =>
            data.error
              ? {
                  quoteData: { ic_data: [{ id: 0 }], data: [], status: false },
                  isQuoteLoading: false,
                }
              : { quoteData: data?.data?.data, isQuoteLoading: data?.isLoading }
          )
      : [],
    (data) => data?.quoteData?.ic_data[0]?.id
  );
  return {
    quotes,
    sortedKeys,
    quotesByInsurer,
    allQuotes: allQuotesData
      ? allQuotesData
          .filter((item) => item?.data?.data.status)
          .map((item) => item?.data?.data)
      : [],
    count: sortedKeys
      ?.flatMap((key) => quotes?.[key]?.map((item) => item))
      ?.filter((item) => item?.quoteData?.status)?.length,
    isAnyQuoteLoading: allQuotesData?.some((item) => item.isLoading),
    isPlanLoading,
  };
};
export const useStorePlan = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    (req) => api.post(`/store/selected/plan`, req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["Details"]);
        navigate({ pathname: "/proposal", search: location.search });
      },
    }
  );
  return { storePlan: mutate, isStoredPlanLoading: isLoading };
};
export const useShortlistPlan = () => {
  const { mutate } = useMutation((req) => api.put(`/travel_shortlisted`, req));
  return { shortlistPlan: mutate };
};
export const useGetKnowMore = () => {
  const knowMoreData = useSelector(knowMoreSelector);
  const product_id = knowMoreData?.data?.[0]?.plan_id;
  const { data, isLoading } = useQuery(
    ["KnowMore", product_id],
    () => api.get(`travel_products/${product_id}/travel_knowmore_features`),
    { enabled: !!product_id }
  );
  return { knowMoreData: data?.data, isKnowMoreLoading: isLoading };
};
export const useGetShortlistPlan = () => {
  const { getQuery } = useUrlQuery();
  const enquiry_id = getQuery("enquiry_id");
  const shortListedQuotes = useSelector(shortListedQuotesSelector);
  const { isAnyQuoteLoading, quotes, allQuotes } = useGetQuotes();
  const { data, isLoading } = useQuery(
    ["Shortlisted", enquiry_id],
    () => api.get(`/travel_shortlisted?enquiry_id=${enquiry_id}`),
    {
      enabled:
        !isAnyQuoteLoading &&
        !!Object.keys(quotes).length &&
        !shortListedQuotes.length,
      onSuccess: ({ data }) => {
        const selectedProducts = data.data.products.map(
          (item) => item.product_id
        );
        allQuotes.forEach((quote) => {
          if (selectedProducts.includes(quote.data[0].plan_id)) {
            Actions.addQuoteToShortlist(quote);
          }
        });
      },
    }
  );

  return { getShortlistedData: data, isShortlistedLoading: isLoading };
};
export const useGetCompareFeature = () => {
  const compareQuote = useSelector(compareQuoteSelector);
  const data = useQueries(
    compareQuote?.map((item) => ({
      queryKey: ["Compare", item?.data?.[0]?.plan_id],
      queryFn: () =>
        api.get(
          `travel_products/${item?.data?.[0]?.plan_id}/travel_compare_features`
        ),
      staleTime: 5000,
    }))
  );
  return {
    comparisonData: data?.map((res) => res?.data?.data),
    isComparisonLoading: data.some((res) => res.isLoading),
  };
};
export const useGetComparePdf = () => {
  const { getQuery } = useUrlQuery();
  const enquiry_id = getQuery("enquiry_id");
  const { data, mutate, isLoading } = useMutation(
    () => api.post(`/travel/compare/pdf_link`, { enquiry_id: enquiry_id }),
    { onSuccess: (data) => window.open(data?.data?.data, "_blank") }
  );
  return {
    getComparePdf: mutate,
    pdf: data?.data?.data,
    isComparePdfLoading: isLoading,
  };
};
export const useAddQuoteToCompare = () => {
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useMutation(
    (req) => api.put(`/travel_comparisons`, req),
    { onSuccess: () => queryClient.invalidateQueries(["Compare"]) }
  );
  return { addQuoteToCompare: mutate, isComparisonPlanLoading: isLoading };
};
